/**
 * UI primitives mapped onto the real Regis design-system classes from
 * globals.css (.card / .btn-gold / .btn-outline / .input / .label). No
 * hardcoded colors — everything flows through the navy/gold tokens.
 */

import type { ButtonHTMLAttributes, CSSProperties, InputHTMLAttributes, ReactNode } from 'react';

export function Card({ children, style, className }: { children: ReactNode; style?: CSSProperties; className?: string }) {
  return (
    <div className={`card ${className ?? ''}`} style={style}>
      {children}
    </div>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost';
};

export function Button({ variant = 'primary', className, children, ...rest }: ButtonProps) {
  const cls = variant === 'primary' ? 'btn-gold' : 'btn-outline';
  return (
    <button className={`${cls} ${className ?? ''}`} {...rest}>
      {children}
    </button>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`input ${className ?? ''}`} {...rest} />;
}

export function Label({ children }: { children: ReactNode }) {
  return <span className="label">{children}</span>;
}

export function Muted({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span style={{ color: 'var(--gray-400)', ...style }}>{children}</span>;
}

/**
 * Stat tile — matches the native apps (Swift/Kotlin MacroRing tile): a rounded
 * square (or `wide` rounded rectangle for a lone stat like steps) with a centered
 * value, label, and a color progress bar. No target number; the bar conveys
 * progress. Shadowed; the value uses thousands separators and fits 4 digits.
 */
export function MacroTile({
  value,
  target,
  label,
  color,
  wide = false,
}: {
  value: number;
  target: number;
  label: string;
  color: string;
  wide?: boolean;
}) {
  const pct = target > 0 ? Math.min(value / target, 1) : 0;
  return (
    <div
      className="flex flex-col items-center justify-between rounded-2xl"
      style={{
        background: 'var(--surface-elevated)',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.35)',
        padding: '12px 10px',
        minHeight: wide ? 100 : 92,
      }}
    >
      <p className="font-bold text-white leading-tight text-center tabular-nums" style={{ fontSize: wide ? 30 : 22 }}>
        {Math.round(value).toLocaleString()}
      </p>
      <p className="text-center" style={{ fontSize: 13, color: 'var(--gray-500)' }}>{label}</p>
      <div className="w-full rounded-full mt-2" style={{ height: 5, background: 'var(--navy-light)' }}>
        <div className="h-full rounded-full" style={{ width: `${pct * 100}%`, background: color, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

export function Pill({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'success' | 'warning' | 'error' }) {
  const color =
    tone === 'success' ? 'var(--success)' : tone === 'warning' ? 'var(--warning)' : tone === 'error' ? 'var(--error)' : 'var(--info)';
  return (
    <span
      style={{
        fontSize: '0.72rem',
        fontWeight: 600,
        padding: '0.15rem 0.6rem',
        borderRadius: 'var(--radius-sm)',
        color,
        border: `1px solid ${color}`,
        background: 'transparent',
      }}
    >
      {children}
    </span>
  );
}
